import { useRouter } from 'vue-router';
import { useFavicon } from '@vueuse/core';
import { ImportMetaEnv } from '@/types/env';
import { TemplateConfigStoryblok } from '@/types';
import { ISbPluginThemeColorConfig } from '@/types';
import brandsData from '@/auto-generated/brands.json';
import { replaceCurlyBrackesWithVariable } from '@/utils/text';

type Brands = NonNullable<ImportMetaEnv['VITE_STORYBLOK_BRAND_FOLDER']>;
type BrandsData = {
  // eslint-disable-next-line no-unused-vars
  [key in Brands]: TemplateConfigStoryblok;
};

const isEditor = import.meta.env.MODE === 'editor';
const brandFolder = import.meta.env.VITE_STORYBLOK_BRAND_FOLDER;

// we need to use window.location.pathname to get the correct path on page load until it's mounted and the router is ready
const routePath = ref<string>(window.location.pathname);

const pathAndBrand = computed(() => getBrandAndPath(routePath.value));
const path = computed(() => pathAndBrand.value.path);
const brand = computed(() => pathAndBrand.value.brand);

export default function useConfig() {
  const router = useRouter();
  const favicon = useFavicon();

  onMounted(() => {
    watch(router.currentRoute, () => {
      routePath.value = router.currentRoute.value.path;
    });
  });

  const config = computed(() => {
    if (!brand.value) return null;
    const data = (brandsData as unknown as BrandsData)[brand.value];

    // Ensure the logo field exists
    if (!data?.logo) {
      console.warn(`No logo found for brand: ${brand.value}`);
      return null;
    }

    if (data?.footer?.[0]?.copyrightText) {
      data.footer[0].copyrightText = replaceCurlyBrackesWithVariable(
        data.footer[0].copyrightText,
        'year',
        new Date().getFullYear().toString(),
      );
    }

    return data;
  });

  const moonbugLogo = computed(() => {
    const logo = (brandsData as unknown as BrandsData)?.moonbug?.logo;
    return logo || null;
  });
  const isSubBrand = computed(() => brand.value !== 'moonbug');
  const primaryColor = computed(() => {
    const colorConfig: ISbPluginThemeColorConfig | undefined =
      config.value?.colors;
    return colorConfig?.colors?.[0];
  });

  favicon.value = config.value?.favicon?.filename || '';
  setBrandClass(brand.value);
  watch(config, () => {
    favicon.value = config.value?.favicon?.filename;
    setBrandClass(brand.value);
  });

  return {
    brand,
    path,
    config,
    primaryColor,
    isSubBrand,
    moonbugLogo,
  };
}

const setBrandClass = (brand: Brands) => {
  const body = document.body;
  const brandClass = `brand-${brand}`;

  // remove all brand classes
  body.classList.forEach((className) => {
    if (className.includes('brand-')) body.classList.remove(className);
  });

  body.classList.add(brandClass);
};

export const getBrandAndPath = (routePath: string) => {
  // in the editor get the brand from the path (e.g. /brand/about -> brand)
  if (isEditor) {
    const path = routePath;
    const brandFromSlug = path?.split(
      '/',
    )[1] as ImportMetaEnv['VITE_STORYBLOK_BRAND_FOLDER'];
    return {
      path: routePath,
      brand: brandFromSlug,
    };
  }

  // on live site use brand from env since the brand doesn't exist in the path
  return {
    path: routePath,
    brand: brandFolder,
  };
};
